import { forwardRef, type HTMLAttributes } from 'react'

type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12
type Align = 'start' | 'center' | 'end' | 'stretch'

interface StackProps extends HTMLAttributes<HTMLDivElement> {
  gap?: Gap
  align?: Align
  as?: 'div' | 'section' | 'article' | 'main' | 'aside' | 'nav'
}

const gapClasses: Record<Gap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
}

const alignClasses: Record<Align, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
}

/**
 * Stack - Vertical flex container with consistent gap spacing
 *
 * @example
 * <Stack gap={4} align="center">
 *   <Heading level={2}>Title</Heading>
 *   <Text>Description</Text>
 * </Stack>
 */
const Stack = forwardRef<HTMLDivElement, StackProps>(
  ({ gap = 4, align = 'stretch', as: Component = 'div', className = '', children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={`flex flex-col ${gapClasses[gap]} ${alignClasses[align]} ${className}`}
        {...props}
      >
        {children}
      </Component>
    )
  }
)

Stack.displayName = 'Stack'

export default Stack
